import { z } from 'zod';
import { Company, Site } from './company.schemas';
import { VisitContext } from './visit.schemas';

// ============================================================================
// REPORT SCHEMAS
// ============================================================================

/**
 * Variable Resolution Context
 */
export const DataContext = z
  .object({
    company: Company.optional(),
    site: Site.optional(),
    visit: VisitContext.optional(),
    variables: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

/**
 * Highlight metadata for changed values
 */
export const ValueHighlight = z.object({
  previousValue: z.unknown().optional(),
  reason: z.string().optional(),
  context: z.string().optional(),
});

export const ReportCommentMedia = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    dataUrl: z.string().optional(),
    storagePath: z.string().optional(),
    mimeType: z.string().optional(),
    byteSize: z.number().optional(),
    createdAt: z.string(),
  })
  .refine((value) => value.dataUrl || value.storagePath, {
    message: 'ReportCommentMedia requires dataUrl or storagePath.',
  });

export const ReportSectionComment = z.object({
  text: z.string().optional(),
  updatedAt: z.string(),
  media: z.array(ReportCommentMedia).optional(),
});

export const NonConformity = z.object({
  id: z.string(),
  code: z.string(),
  description: z.string(),
  severity: z.enum(['low', 'medium', 'high']),
  dueDate: z.string().optional(),
  correctiveAction: z.string().optional(),
  owner: z.string().optional(),
  sectionId: z.string().optional(),
  status: z.enum(['open', 'in-progress', 'closed']).default('open'),
  notes: z.string().optional(),
});

/**
 * Report with flat data model
 */
export const Report = z.object({
  id: z.string(),
  version: z.literal(4),

  // Template linkage
  templateId: z.string(),
  templateVersion: z.string(), // "2025-09-27-v2" - immutable

  // Ownership
  companyId: z.string(),
  siteId: z.string(),

  // Lifecycle
  status: z.enum(['draft', 'finalized']).default('draft'),
  createdAt: z.string(),
  updatedAt: z.string(),
  finalizedAt: z.string().optional(),
  previousReportId: z.string().optional(),

  // Scheduling
  plannedVisitAt: z.string().optional(),
  reportAt: z.string().optional(),

  // Metadata
  meta: z.object({
    title: z.string(),
    visitDate: z.string(),
    inspector: z.string(),
    auditType: z.string().optional(),
    gas: z.string().optional(),
    company: z.string().optional(),
    siteName: z.string().optional(),
    siteLocation: z.string().optional(),
    applicableSections: z.array(z.string()).optional(),
    plannedPeriodSnapshot: z.string().optional(),
    plannedDateSnapshot: z.string().optional(),
  }),

  // THE DATA - Flat structure, single source of truth
  data: z.object({
    // Keypath → value mapping
    values: z.record(z.string(), z.unknown()).default({}),

    // Change tracking (compared to previousReportId)
    highlights: z.record(z.string(), ValueHighlight).optional(),

    // Section comments keyed by ViewSection.id
    comments: z.record(z.string(), ReportSectionComment).default({}),

    // Attachment storage paths referenced by the report (Supabase Storage)
    attachmentUrls: z.array(z.string()).optional(),

    // Non-conformities
    nonConformities: z
      .array(
        z.object({
          id: z.string(),
          fieldPath: z.string().optional(), // Links to values key
          severity: z.enum(['low', 'medium', 'high']),
          description: z.string(),
          dueDate: z.string().optional(),
          correctiveAction: z.string().optional(),
          owner: z.string().optional(),
          status: z.enum(['open', 'in-progress', 'closed']).default('open'),
          notes: z.string().optional(),
        })
      )
      .default([]),
  }),
});

// Export types
export type DataContext = z.infer<typeof DataContext>;
export type ValueHighlight = z.infer<typeof ValueHighlight>;
export type ReportCommentMedia = z.infer<typeof ReportCommentMedia>;
export type ReportSectionComment = z.infer<typeof ReportSectionComment>;
export type NonConformity = z.infer<typeof NonConformity>;
export type Report = z.infer<typeof Report>;
